import { lazy, Suspense, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn, Skeleton } from '@/design-system';
import { isRtl } from '@/shared/i18n/locales';

import {
  donutAllowed,
  formatChartValue,
  isBlank,
  peak,
  plotRows,
  seriesStyles,
  TONE_TOKEN,
  type ChartDatum,
  type ChartSeries,
  type ChartTone,
  type ChartValue,
  type ChartValueKind,
  type MarkerShape,
  type SeriesStyle,
} from './model';

// Recharts is split into its own chunk and fetched the first time a chart renders.
const TrendPlot = lazy(() => import('./impl/plots').then((m) => ({ default: m.TrendPlot })));
const BarsPlot = lazy(() => import('./impl/plots').then((m) => ({ default: m.BarsPlot })));
const DonutPlot = lazy(() => import('./impl/plots').then((m) => ({ default: m.DonutPlot })));

export interface ChartBaseProps {
  /** What the chart shows ("Submissions by month"). */
  title: string;
  /** Optional one-line caption: the definition or the question it answers. */
  caption?: ReactNode;
  data: readonly ChartDatum[];
  series: readonly ChartSeries[];
  kind: ChartValueKind;
  /** Plot height in px (default 200). */
  height?: number;
  /** Drill-through: called with the datum and series of a clicked mark. */
  onSelect?: (datum: ChartDatum, seriesKey: string) => void;
  /** Category column header of the text alternative ("Bank", "Month"). */
  categoryLabel?: string;
  /** The series is still loading (a skeleton at the plot height, not "no figures"). */
  loading?: boolean;
  className?: string;
}

/** Legend swatch: the series colour with its marker shape (lines also show the dash) — never colour alone. */
function Swatch({ style, line }: { style: Pick<SeriesStyle, 'color' | 'marker' | 'dash'>; line: boolean }) {
  const c = style.color;
  const m: MarkerShape = style.marker;
  const shape =
    m === 'square' ? (
      <rect x={7} y={2} width={8} height={8} fill={line ? 'var(--color-surface)' : c} stroke={c} strokeWidth={2} />
    ) : m === 'triangle' ? (
      <path d="M11 1.5 15.5 10h-9z" fill={line ? 'var(--color-surface)' : c} stroke={c} strokeWidth={2} />
    ) : m === 'diamond' ? (
      <path d="M11 1 16 6 11 11 6 6z" fill={line ? 'var(--color-surface)' : c} stroke={c} strokeWidth={2} />
    ) : m === 'cross' ? (
      <path d="M7.5 2.5l7 7M14.5 2.5l-7 7" stroke={c} strokeWidth={2} />
    ) : (
      <circle cx={11} cy={6} r={4} fill={line ? 'var(--color-surface)' : c} stroke={c} strokeWidth={2} />
    );
  return (
    <svg width={22} height={12} viewBox="0 0 22 12" aria-hidden="true" className="shrink-0">
      {line ? <line x1={0} y1={6} x2={22} y2={6} stroke={c} strokeWidth={2} strokeDasharray={style.dash} /> : null}
      {shape}
    </svg>
  );
}

function Legend({ items, line }: { items: readonly Pick<SeriesStyle, 'key' | 'label' | 'color' | 'marker' | 'dash'>[]; line: boolean }) {
  if (items.length < 2) return null;
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-meta text-ink-secondary">
      {items.map((s) => (
        <li key={s.key} className="inline-flex items-center gap-1.5">
          <Swatch style={s} line={line} />
          {s.label}
        </li>
      ))}
    </ul>
  );
}

function useRtl() {
  const { i18n } = useTranslation();
  return isRtl(i18n.language);
}

/** Tooltip body: the category, then each series with its marker and FULL value (AED amounts never abbreviated). */
function TooltipRows({ datum, styles, kind, targetLabel }: { datum: ChartDatum; styles: readonly SeriesStyle[]; kind: ChartValueKind; targetLabel?: string | undefined }) {
  const { t } = useTranslation();
  const rtl = useRtl();
  return (
    <div dir={rtl ? 'rtl' : 'ltr'}>
      <p className="mb-1 font-medium text-ink">{datum.label}</p>
      <dl className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-0.5">
        {styles.map((s) => (
          <ValueLine key={s.key} label={s.label} swatch={<Swatch style={s} line={false} />} value={datum.values[s.key]} kind={kind} />
        ))}
        {datum.target !== undefined ? <ValueLine label={targetLabel ?? t('charts.target')} swatch={<span className="mx-auto block h-3 w-0.5 bg-ink" aria-hidden="true" />} value={datum.target} kind={kind} /> : null}
      </dl>
    </div>
  );
}

function ValueLine({ label, swatch, value, kind }: { label: string; swatch: ReactNode; value: ChartValue; kind: ChartValueKind }) {
  const { t } = useTranslation();
  return (
    <>
      <dt className="contents">
        {swatch}
        <span className="text-ink-secondary">{label}</span>
      </dt>
      <dd className="tabular text-end text-ink">
        <bdi dir="ltr">
          {kind === 'money' && value !== null && value !== undefined ? <span className="me-1 text-ink-muted">{t('charts.currency')}</span> : null}
          {formatChartValue(value, kind)}
        </bdi>
      </dd>
    </>
  );
}

/** Visually hidden text alternative: the figures behind the chart as a real table. */
function DataTable({ title, data, styles, kind, categoryLabel, targetLabel }: { title: string; data: readonly ChartDatum[]; styles: readonly SeriesStyle[]; kind: ChartValueKind; categoryLabel: string; targetLabel?: string | undefined }) {
  const { t } = useTranslation();
  const withTarget = data.some((d) => d.target !== undefined);
  return (
    <table className="sr-only">
      <caption>{t('charts.dataTable', { title })}</caption>
      <thead>
        <tr>
          <th scope="col">{categoryLabel}</th>
          {styles.map((s) => (
            <th key={s.key} scope="col">
              {kind === 'money' ? t('charts.inCurrency', { label: s.label }) : s.label}
            </th>
          ))}
          {withTarget ? <th scope="col">{targetLabel ?? t('charts.target')}</th> : null}
        </tr>
      </thead>
      <tbody>
        {data.map((d) => (
          <tr key={d.key}>
            <th scope="row">{d.label}</th>
            {styles.map((s) => (
              <td key={s.key}>{formatChartValue(d.values[s.key], kind)}</td>
            ))}
            {withTarget ? <td>{formatChartValue(d.target, kind)}</td> : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** One-sentence summary for role="img": what, how many, which is largest. */
function useSummary(title: string, data: readonly ChartDatum[], series: readonly ChartSeries[], kind: ChartValueKind) {
  const { t } = useTranslation();
  const first = series[0];
  const top = first ? peak(data, first.key) : null;
  const base = t('charts.summary', { title, count: data.length, series: series.map((s) => s.label).join(', ') });
  return top && first ? `${base} ${t('charts.peak', { series: first.label, label: top.label, value: formatChartValue(top.values[first.key], kind) })}` : base;
}

/**
 * The frame every chart shares: title + caption, legend (markers, not colour alone), the plot as role="img" with a
 * summary label, and the data as a visually hidden table. Empty data shows one plain sentence at the same height.
 */
function ChartFigure({ title, caption, height, legend, summary, blank, table, children, className, loading }: { title: string; caption?: ReactNode; height: number; legend: ReactNode; summary: string; blank: boolean; table: ReactNode; children: ReactNode; className?: string | undefined; loading?: boolean | undefined }) {
  const { t } = useTranslation();
  return (
    <figure className={cn('m-0 flex min-w-0 flex-col gap-2', className)} data-chart="">
      <figcaption className="flex flex-col gap-0.5">
        <span className="text-h3 text-ink">{title}</span>
        {caption ? <span className="text-meta text-ink-muted">{caption}</span> : null}
      </figcaption>
      {blank ? null : legend}
      {loading ? (
        <div style={{ height }}>
          <Skeleton className="h-full w-full" />
        </div>
      ) : blank ? (
        <p className="flex items-center text-meta text-ink-muted" style={{ height }}>
          {t('charts.empty')}
        </p>
      ) : (
        // The SVG is laid out in LTR coordinates (text-anchor is direction-relative in SVG); RTL is handled by
        // reversing the axes (model.axisLayout), so the plot reads from the inline start in Arabic too.
        <div role="img" aria-label={summary} dir="ltr" className="min-w-0" style={{ height }}>
          <Suspense fallback={<Skeleton className="h-full w-full" />}>{children}</Suspense>
        </div>
      )}
      {blank ? null : table}
    </figure>
  );
}

function useCartesian(props: ChartBaseProps) {
  const { t } = useTranslation();
  const styles = useMemo(() => seriesStyles(props.series), [props.series]);
  const rows = useMemo(() => plotRows(props.data, props.series), [props.data, props.series]);
  const summary = useSummary(props.title, props.data, props.series, props.kind);
  const tooltip = (i: number) => {
    const d = props.data[i];
    return d ? <TooltipRows datum={d} styles={styles} kind={props.kind} /> : null;
  };
  const select = props.onSelect ? (i: number, key: string) => { const d = props.data[i]; if (d) props.onSelect?.(d, key); } : undefined;
  const table = <DataTable title={props.title} data={props.data} styles={styles} kind={props.kind} categoryLabel={props.categoryLabel ?? t('charts.category')} />;
  return { styles, rows, summary, tooltip, select, table, blank: isBlank(props.data, props.series) };
}

/** Change over time (months oldest → newest). One value axis; series differ by colour, dash and marker. */
export function TrendChart(props: ChartBaseProps) {
  const rtl = useRtl();
  const c = useCartesian(props);
  const height = props.height ?? 200;
  return (
    <ChartFigure title={props.title} caption={props.caption} height={height} legend={<Legend items={c.styles} line />} summary={c.summary} blank={c.blank} table={c.table} className={props.className} loading={props.loading}>
      <TrendPlot rows={c.rows} series={c.styles} kind={props.kind} rtl={rtl} height={height} tooltip={c.tooltip} onSelect={c.select} />
    </ChartFigure>
  );
}

export interface BarChartProps extends ChartBaseProps {
  /** Horizontal bars (rankings, long names). Default: columns. */
  horizontal?: boolean;
  /** Stacked composition (status mix, ageing buckets). */
  stacked?: boolean;
  /** Draws each datum's `target` as a thin marker on its bar. */
  showTarget?: boolean;
  targetLabel?: string;
  /** Reference line on the value axis (e.g. 100 for "100 % achieved"). */
  reference?: number;
  /** Category axis width for horizontal bars (default 120). */
  categoryWidth?: number;
}

/** Comparison (grouped / horizontal bars, optional target marker) or composition (stacked). */
export function BarChart({ horizontal = false, stacked = false, showTarget = false, targetLabel, reference, categoryWidth = 120, ...props }: BarChartProps) {
  const rtl = useRtl();
  const c = useCartesian(props);
  const height = props.height ?? (horizontal ? Math.max(120, props.data.length * 26 + 40) : 200);
  const tooltip = (i: number) => {
    const d = props.data[i];
    return d ? <TooltipRows datum={d} styles={c.styles} kind={props.kind} targetLabel={targetLabel} /> : null;
  };
  const legendItems = showTarget ? [...c.styles, { key: '__target', label: targetLabel ?? '', color: 'var(--color-ink)', marker: 'square' as const, dash: undefined }] : c.styles;
  return (
    <ChartFigure title={props.title} caption={props.caption} height={height} legend={<Legend items={legendItems} line={false} />} summary={c.summary} blank={c.blank} table={c.table} className={props.className} loading={props.loading}>
      <BarsPlot rows={c.rows} series={c.styles} kind={props.kind} rtl={rtl} height={height} tooltip={tooltip} onSelect={c.select} horizontal={horizontal} stacked={stacked} target={showTarget} reference={reference} categoryWidth={categoryWidth} />
    </ChartFigure>
  );
}

/** Composition across categories (status mix, ageing). A stacked BarChart with its own name at call sites. */
export function StackedBarChart(props: Omit<BarChartProps, 'stacked' | 'showTarget'>) {
  return <BarChart {...props} stacked />;
}

export interface DonutSlice {
  key: string;
  label: string;
  value: ChartValue;
  tone: ChartTone;
}

/** Part-to-whole of at most five slices (more → use a bar chart). The legend lists every slice with its value. */
export function DonutChart({ title, caption, slices, kind, height = 180, onSelect, className }: { title: string; caption?: ReactNode; slices: readonly DonutSlice[]; kind: ChartValueKind; height?: number; onSelect?: (slice: DonutSlice) => void; className?: string }) {
  const { t } = useTranslation();
  const data = useMemo<ChartDatum[]>(() => slices.map((s) => ({ key: s.key, label: s.label, values: { value: s.value } })), [slices]);
  const series = useMemo<ChartSeries[]>(() => [{ key: 'value', label: title, tone: 'neutral' }], [title]);
  const styles = useMemo(() => seriesStyles(series), [series]);
  const rows = useMemo(() => plotRows(data, series), [data, series]);
  const summary = useSummary(title, data, series, kind);
  if (!donutAllowed(data)) throw new Error('DonutChart: use a bar chart for more than five categories.');
  const sliceStyles = slices.map((s, i) => ({ key: s.key, label: `${s.label} · ${formatChartValue(s.value, kind)}`, color: TONE_TOKEN[s.tone], marker: (['circle', 'square', 'triangle', 'diamond', 'cross'] as const)[i] ?? 'circle', dash: undefined }));
  const tooltip = (i: number) => {
    const s = slices[i];
    const base = styles[0];
    return s && base ? <TooltipRows datum={{ key: s.key, label: s.label, values: { value: s.value } }} styles={[{ ...base, label: title, color: TONE_TOKEN[s.tone] }]} kind={kind} /> : null;
  };
  return (
    <ChartFigure
      title={title}
      caption={caption}
      height={height}
      legend={<Legend items={sliceStyles} line={false} />}
      summary={summary}
      blank={isBlank(data, series)}
      table={<DataTable title={title} data={data} styles={styles} kind={kind} categoryLabel={t('charts.category')} />}
      className={className}
    >
      <DonutPlot rows={rows} series={styles} height={height} tooltip={tooltip} colors={slices.map((s) => TONE_TOKEN[s.tone])} onSelect={onSelect ? (i) => { const s = slices[i]; if (s) onSelect(s); } : undefined} />
    </ChartFigure>
  );
}
